import { useAccount } from "@qw/auth";

import {
  type InboxConversationFragment,
  type InboxConversationQuery,
  useInboxConversationMessageBodiesQuery,
  useInboxConversationQuery,
  useInboxConversationUpdatedSubscription,
} from "@qw/graphql";
import { KnownInboxLabel } from "@qw/qw-common";
import { type PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { useInboxQueryStates } from "./inbox-query-states-provider";
import { ConversationContext, type ConversationContextValue } from "./use-conversation";
import { useRemoveLabelFromConversation } from "./use-remove-label-from-conversation";

export interface ConversationProviderProps {
  conversation?: InboxConversationFragment;
  externalId?: string;
}

export const ConversationProvider = ({
  conversation,
  children,
  externalId,
}: PropsWithChildren<ConversationProviderProps>) => {
  const { impersonatorEmail, userInbox } = useAccount();
  const { conversationId } = useInboxQueryStates();
  const [displayFooters, setDisplayFooters] = useState(false);
  const [displayHeaders, setDisplayHeaders] = useState(false);
  const [removeLabel] = useRemoveLabelFromConversation();
  const conversationLookupId = externalId ?? conversationId;
  const { data, error, loading, refetch } = useInboxConversationQuery({
    variables: {
      input: externalId ? { externalId } : { conversationId: conversationId ?? "" },
    },
    skip: !!conversation || !conversationLookupId,
    onCompleted: (data: InboxConversationQuery) => {
      if (
        conversationLookupId &&
        // Only remove the UNREAD label if the user is NOT impersonating another user
        // AND the conversation IS in the user's inbox.
        !impersonatorEmail &&
        data?.inboxConversation?.inboxId === userInbox?.id &&
        data?.inboxConversation?.labels.includes(KnownInboxLabel.UNREAD)
      ) {
        removeLabel(data.inboxConversation, KnownInboxLabel.UNREAD);
      }
    },
  });

  const inboxConversation = conversation ?? data?.inboxConversation;

  // One batched fetch for every message body in the thread, replacing the per-message
  // `InboxMessage` query each `MessageDetail` used to fire (B-1824). `cache-first` matches what
  // that query used: bodies are immutable once sent, so revisiting a thread must not re-charge the
  // provider fetch. Keyed off the resolved conversation so the `conversation`-prop path works too.
  const {
    data: messageBodiesData,
    error: messageBodiesError,
    loading: messageBodiesLoading,
    refetch: refetchMessageBodies,
  } = useInboxConversationMessageBodiesQuery({
    variables: { input: { conversationId: inboxConversation?.id ?? "" } },
    skip: !inboxConversation?.id,
    fetchPolicy: "cache-first",
    // Without this Apollo reports `loading: false` for the whole of a `refetch()` (it only notifies
    // on the final result), so the reconcile below would leave a just-arrived message rendering
    // "message not found" until the round trip landed. The per-message query this replaced was a
    // fresh observable whose first fetch always reported `loading`.
    notifyOnNetworkStatusChange: true,
  });

  const messageBodiesById = useMemo(
    () => new Map((messageBodiesData?.inboxConversationMessageBodies ?? []).map((message) => [message.id, message])),
    [messageBodiesData]
  );

  // A message that arrives after the batch resolved — an inbound reply, or one the viewer just
  // sent — is in `inboxConversation.messages` with no body to render. Reconcile by refetching the
  // batch, but only once per distinct set of missing ids: a message the server legitimately
  // withholds stays missing forever, and an unguarded "refetch while anything is missing" effect
  // would spin on it. Deliberately not wired to `inboxConversationUpdated` below, which also fires
  // on label and read-state churn — each of those refetches would be another provider thread fetch.
  const missingBodyKey = (inboxConversation?.messages ?? [])
    .filter((message) => !messageBodiesById.has(message.id))
    .map((message) => message.id)
    .join(",");
  const reconciledMissingBodyKey = useRef<string | null>(null);

  useEffect(() => {
    // `messageBodiesData` gates the very first render, where every id is trivially "missing"
    // because the batch has not answered yet.
    if (!messageBodiesData || messageBodiesLoading || !missingBodyKey) {
      return;
    }

    if (reconciledMissingBodyKey.current === missingBodyKey) {
      return;
    }

    reconciledMissingBodyKey.current = missingBodyKey;
    void refetchMessageBodies();
  }, [messageBodiesData, messageBodiesLoading, missingBodyKey, refetchMessageBodies]);

  const subscriptionConversationId = inboxConversation?.id ?? (!externalId ? conversationId : undefined);
  // A conversation in the user's own inbox is always replyable. A conversation in *another* user's
  // inbox is replyable only when the user is an active collaborator on its deal — the same predicate
  // the backend reply guards enforce, surfaced as `viewerCanReplyAsCollaborator` so the reply UI
  // matches actual authorization (read access is broader than reply access, so foreign-inbox
  // visibility alone must not unlock replying).
  const isOwnInbox = !!inboxConversation && inboxConversation.inboxId === userInbox?.id;
  const isReadOnly = !!inboxConversation && !isOwnInbox && !inboxConversation.viewerCanReplyAsCollaborator;
  // Replyable, but the thread lives in another user's inbox: the user is responding as a collaborator
  // to a thread they were not originally on. Drives the "not originally on this thread" notice.
  const isCollaboratorReply = !!inboxConversation && !isOwnInbox && !!inboxConversation.viewerCanReplyAsCollaborator;
  const subscriptionInboxEmail = inboxConversation?.inbox.email;

  useInboxConversationUpdatedSubscription({
    variables: {
      conversationId: subscriptionConversationId ?? "",
      ...(subscriptionInboxEmail ? { email: subscriptionInboxEmail } : {}),
    },
    onData: () => {
      refetch();
    },
    skip: !!conversation || !subscriptionConversationId,
  });

  const value: ConversationContextValue = {
    conversationId: inboxConversation?.id,
    displayFooters,
    displayHeaders,
    error,
    inboxConversation,
    loading,
    messageBodiesById,
    messageBodiesError,
    messageBodiesLoading,
    refetchQueries: ["InboxConversation"],
    refetch,
    setDisplayFooters,
    setDisplayHeaders,
    isOwnInbox,
    isReadOnly,
    isCollaboratorReply,
  };

  return <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>;
};
